import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { Compass, Loader2, ShieldAlert } from "lucide-react";
import { useStoryArchitect } from "./useStoryArchitect";
import { StoryContextStatus } from "./StoryContextStatus";
import { ProposalCards } from "./ProposalCards";

const PLACEMENTS = [
  { value: "", label: "Let Shweebum recommend" },
  { value: "beginning", label: "Beginning" },
  { value: "early_middle", label: "Early middle" },
  { value: "midpoint", label: "Midpoint" },
  { value: "late_middle", label: "Late middle" },
  { value: "ending", label: "Ending" },
  { value: "specific", label: "A specific chapter…" },
];

export default function StoryArchitectModal({ open, onOpenChange, projectId }) {
  const { knowledge, fetchStatus, analyze, analyzing, result, error, reset } = useStoryArchitect(projectId);
  const [goal, setGoal] = useState("");
  const [placement, setPlacement] = useState("");
  const [chapterNum, setChapterNum] = useState("");

  useEffect(() => {
    if (open) fetchStatus();
  }, [open, fetchStatus]);

  const submit = () => {
    if (goal.trim().length < 10 || analyzing) return;
    const specific = placement === "specific" && chapterNum ? parseInt(chapterNum, 10) : null;
    analyze(goal.trim(), placement === "specific" ? "" : placement, specific);
  };

  const canonConcerns = result?.canon_precheck?.concerns || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-[#0D1525] border border-indigo-500/30 text-slate-100" data-testid="story-architect-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-indigo-200">
            <Compass className="w-5 h-5 text-indigo-400" /> Story Architect
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Describe what you want to make happen, and Shweebum will study your story and suggest ways to build it.
            Suggestions are ideas only — nothing is added to your story.
          </DialogDescription>
        </DialogHeader>

        <StoryContextStatus knowledge={knowledge} />

        {!result && (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-200">What are you trying to make happen?</label>
              <textarea
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder='e.g. "I know Sarah discovers the truth in Chapter 15. Where should I start planting clues?"'
                rows={4}
                className="mt-1.5 w-full rounded-lg bg-slate-900/80 border border-slate-700/70 focus:border-indigo-500/60 focus:outline-none p-3 text-sm text-slate-100 placeholder:text-slate-500"
                data-testid="architect-goal-input"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-slate-400">Where should it happen?</label>
              <select
                value={placement}
                onChange={(e) => setPlacement(e.target.value)}
                className="rounded-md bg-slate-900/80 border border-slate-700/70 text-xs text-slate-200 px-2 py-1.5"
                data-testid="architect-placement-select"
              >
                {PLACEMENTS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              {placement === "specific" && (
                <input
                  type="number"
                  min="1"
                  value={chapterNum}
                  onChange={(e) => setChapterNum(e.target.value)}
                  placeholder="Ch #"
                  className="w-20 rounded-md bg-slate-900/80 border border-slate-700/70 text-xs text-slate-200 px-2 py-1.5"
                  data-testid="architect-chapter-input"
                />
              )}
            </div>
            {error && (
              <p className="text-xs text-rose-300 bg-rose-950/40 border border-rose-500/30 rounded-lg p-2.5" data-testid="architect-error">
                {error}
              </p>
            )}
            <button
              onClick={submit}
              disabled={goal.trim().length < 10 || analyzing}
              className="w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="architect-analyze-btn"
            >
              {analyzing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Studying your story…</>
              ) : (
                <><Compass className="w-4 h-4" /> Analyze My Story</>
              )}
            </button>
            {analyzing && (
              <p className="text-center text-[11px] text-slate-500">
                Reading your characters, outline, Story Bible and manuscript state. This takes about a minute.
              </p>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-100" data-testid="architect-results-heading">
                Here {result.proposals.length === 1 ? "is one way" : `are ${result.proposals.length} ways`} this could work
              </h3>
              <button
                onClick={() => { reset(); }}
                className="text-xs text-indigo-300 hover:text-indigo-200"
                data-testid="architect-new-question-btn"
              >
                Ask something else
              </button>
            </div>
            {canonConcerns.length > 0 && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-950/30 p-3" data-testid="architect-canon-warning">
                <p className="flex items-center gap-1.5 text-xs font-medium text-rose-200">
                  <ShieldAlert className="w-4 h-4" /> Possible canon concerns detected
                </p>
                <ul className="mt-1.5 space-y-1">
                  {canonConcerns.map((c, i) => (
                    <li key={i} className="text-[11px] text-rose-300/90">
                      • ({c.certainty}) {c.description}{c.contradicts ? ` — may conflict with: ${c.contradicts}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <ProposalCards proposals={result.proposals} />
            <p className="text-[11px] text-slate-500 text-center">
              These are ideas only. Your manuscript, outline and Story Bible have not been changed.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
